import { IconButton, VolumeSlider } from '../../ui';
import type { MiniDrag } from '../../playback/dock';
import { MuteIcon, PlayIcon } from './PlayerChrome';
import { useNarrowViewport, type WatchBinding } from './useWatchScreen';
import { useMiniSwipe } from './miniSwipe';
import { useCopy } from '../../strings/useCopy';
import styles from './miniChrome.module.css';

/**
 * The minimized player's chrome — four controls and a progress line, and deliberately not a
 * smaller `PlayerChrome`.
 *
 * The full bar is eleven controls, four menus and a scrub bar with storyboard previews on it. None of
 * that survives a 320 px box: a menu taller than the picture is the clipping trap `docs/specs/web-ui.md`
 * documents, and a scrub bar whose whole width is 320 px puts every chapter inside two pixels. So the
 * minimized player states the two things a corner player is for — is it playing, how loud — plus the
 * two window acts, and everything else is one press away on the page it came from.
 *
 * The progress line is **not** a scrub bar for that reason: it reports, and it is `aria-hidden`
 * because the position is already the media session's and the watch page's to speak.
 *
 * The two shapes are the same elements moved rather than two sets of controls, but two things about
 * them are decided *here* rather than in the stylesheet, because CSS cannot reach either:
 *
 * - the button **variant**. On the popover these sit on video and take `media`'s fixed light ink; on
 *   the bar they sit on `surface-raised`, which is `#ffffff` in the light theme — the same ink there
 *   is white on white. A media query cannot change a variant, so the boundary is read twice, from
 *   one token (`useNarrowViewport`).
 * - the **swipe** directions, for the reason core's `decideSwipe` documents: the popover has an edge
 *   to be pushed off sideways and the full-width bar does not.
 */
function MaximizeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      {/* The fullscreen glyph's outward arrows, one diagonal pair rather than four corners: this
          restores a window rather than filling the screen, so it says "bigger", not "everything". */}
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14 4h6v6M20 4l-7 7M10 20H4v-6M4 20l7-7"
      />
    </svg>
  );
}

/**
 * A step through the list: the transport triangle against a bar, the same picture the watch page's
 * own panel steps with (`PlaylistPanel`'s `StepGlyph`). Drawn here as well rather than shared,
 * which is this layer's rule for a glyph one component draws.
 */
function StepIcon({ direction }: { readonly direction: 'back' | 'forward' }) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <g transform={direction === 'forward' ? undefined : 'translate(24 0) scale(-1 1)'}>
        <path fill="currentColor" d="M6 5.5 15.5 12 6 18.5z" />
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          d="M17.8 5.5v13"
        />
      </g>
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        d="M6 6l12 12M18 6 6 18"
      />
    </svg>
  );
}

export function MiniControls({
  binding,
  onMaximize,
  onClose,
  closesQueue,
  follow,
}: {
  readonly binding: WatchBinding;
  readonly onMaximize: () => void;
  readonly onClose: () => void;
  /**
   * There is a queue, so the press that closes this box also ends the list (2026-08-08,
   * `playback/PlaybackHost.tsx`). It changes only what the ✕ is *called*: a control that deletes
   * something somebody built has to say so, and the two names are the catalogue's.
   */
  readonly closesQueue: boolean;
  readonly follow: (drag: MiniDrag | null) => void;
}) {
  const words = useCopy().text.watch;
  const { transport, handlers } = binding;
  // Muted reads as zero on the slider — the level is remembered, the bar states what is audible.
  const level = transport.muted ? 0 : transport.volume;
  const narrow = useNarrowViewport();
  const variant = narrow ? 'ghost' : 'media';
  const swipe = useMiniSwipe({ sideways: !narrow, onClose, onMaximize, follow });
  // `null` while this player is one video rather than a list — a queue or a playlist is what puts
  // the two step buttons on the strip at all.
  const step = binding.playlist === null ? null : binding.listStep;

  return (
    /*
     * The overlay covers the whole surface so that hovering the *picture* reveals the controls, not
     * only hovering the strip they sit in. It is also why the reveal cannot be `:hover` on the strip
     * itself: a faded strip is what the pointer is trying to find. Being the whole surface is also
     * what makes it the right element to read a swipe off.
     */
    <div className={styles.overlay} {...swipe}>
      <div className={styles.controls}>
        {/*
         * The two steps through the list, either side of Play — drawn only when there *is* a list,
         * so a corner player holding one video keeps the strip it always had. Neither navigates:
         * they change what is in the corner and leave the reader on the page they are reading
         * (`useWatchScreen`'s `listStep`). Disabled rather than dropped at the ends of the list, so
         * the strip does not reshape itself under the pointer between two videos.
         */}
        {step === null ? null : (
          <IconButton
            variant={variant}
            disabled={step.previous === null}
            onClick={step.previous ?? undefined}
            label={words.mini.previous}
          >
            <StepIcon direction="back" />
          </IconButton>
        )}

        <IconButton
          variant={variant}
          onClick={handlers.togglePlay}
          label={transport.playing ? words.chrome.pause : words.chrome.play}
        >
          <PlayIcon playing={transport.playing} />
        </IconButton>

        {step === null ? null : (
          <IconButton
            variant={variant}
            disabled={step.next === null}
            onClick={step.next ?? undefined}
            label={words.mini.next}
          >
            <StepIcon direction="forward" />
          </IconButton>
        )}

        <div className={styles.volumeGroup}>
          <IconButton
            variant={variant}
            onClick={handlers.toggleMute}
            label={transport.muted ? words.chrome.unmute : words.chrome.mute}
          >
            <MuteIcon muted={transport.muted} volume={transport.volume} />
          </IconButton>
          {/* Collapsed until the group is hovered, the watch bar's own behaviour and now its own
              rule (`ui/volume.module.css`). Gone entirely on the bar below `md`, where there is no
              room for it beside a tile and a mute toggle already answers the only question a bar
              can ask. */}
          <VolumeSlider
            className={styles.volume}
            level={level}
            onChange={handlers.setVolume}
            label={words.chrome.volume}
          />
        </div>

        {/* Takes the slack, in both shapes: Maximize sits at the far end of the strip and the space
            in front of it is what puts it there. */}
        <span className={styles.spacer} />

        <IconButton variant={variant} onClick={onMaximize} label={words.mini.maximize}>
          <MaximizeIcon />
        </IconButton>
      </div>

      {/*
       * Outside the strip, and after it so it is the strip's neighbour rather than its last control.
       * On the popover it is the top-right corner — away from the transport, because it is the one
       * press here that cannot be undone by pressing it again. On the bar the same element is simply
       * the item after the strip, which is the far end: `| [V] P A    M X |`.
       */}
      <IconButton
        className={styles.close}
        variant={variant}
        onClick={onClose}
        label={closesQueue ? words.mini.closeQueue : words.mini.close}
      >
        <CloseIcon />
      </IconButton>

      {/*
       * After the strip in source order so it is drawn over the scrim rather than under it, and
       * outside it so it stays visible while the controls are faded — "how far in am I" is the one
       * thing a minimized player should never make you hover to find out.
       */}
      <div className={styles.progress} aria-hidden="true">
        <span
          className={styles.progressFill}
          style={{ inlineSize: `${String(Math.round(transport.progress * 1000) / 10)}%` }}
        />
      </div>
    </div>
  );
}
